import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getRole, getIsSubscribed, getMustChangePassword } from "../utils/auth";
import ROLES from "../utils/constants";
import LegalReview from "../pages/tenant/LegalReview";
import {
  getRequiredLegalDocuments,
  LegalDocument,
} from "../services/legalDocument.service";

interface LegalGate {
  status: "idle" | "loading" | "error" | "done";
  pending: LegalDocument[];
}

const ProtectedRoute = ({
  children,
  allowedRoles,
}: {
  children: JSX.Element;
  allowedRoles: string[];
}) => {
  const role = getRole();
  const isSubscribed = getIsSubscribed();
  const mustChangePassword = getMustChangePassword();

  // Super admins manage legal content and are never blocked by it.
  const isUser = role !== ROLES.SUPER_ADMIN;

  const [legalGate, setLegalGate] = useState<LegalGate>({
    status: "idle",
    pending: [],
  });

  const runLegalCheck = async () => {
    setLegalGate({ status: "loading", pending: [] });
    try {
      const pending = await getRequiredLegalDocuments();
      setLegalGate({ status: "done", pending });
    } catch {
      setLegalGate({ status: "error", pending: [] });
    }
  };

  useEffect(() => {
    if (!role || !isUser) {
      setLegalGate({ status: "done", pending: [] });
      return;
    }
    let cancelled = false;
    setLegalGate({ status: "loading", pending: [] });
    getRequiredLegalDocuments()
      .then((pending) => {
        if (!cancelled) setLegalGate({ status: "done", pending });
      })
      .catch(() => {
        if (!cancelled) setLegalGate({ status: "error", pending: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [role, isUser]);

  if (!role) {
    return <Navigate to="/login" replace />;
  }

  // Scenario 3: First-login password change not completed - block access
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (role === ROLES.TENANT_ADMIN && !isSubscribed) {
    return <Navigate to="/subscription" replace />;
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/login" replace />;
  }

  // Legal acceptance gate for normal users (backend decides what is required).
  if (isUser) {
    if (legalGate.status === "idle" || legalGate.status === "loading") {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-500 animate-pulse">
              Checking legal documents...
            </p>
          </div>
        </div>
      );
    }

    if (legalGate.status === "error") {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center">
            <p className="text-gray-600 font-medium">
              We could not verify your legal acknowledgements.
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Please try again to continue.
            </p>
            <button
              type="button"
              onClick={runLegalCheck}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (legalGate.pending.length > 0) {
      return (
        <LegalReview
          documents={legalGate.pending}
          onComplete={runLegalCheck}
        />
      );
    }
  }

  return children;
};

export default ProtectedRoute;